// M2.46.A — Modal reutilizable para crear y editar planned workouts.
//
// Usage:
//   <PlannedWorkoutForm mode="create" defaultDate={date} onClose={...} />
//   <PlannedWorkoutForm mode="edit" initial={planned} onClose={...} />
//
// Decisiones:
//   - Modal accesible: focus trap básico via autoFocus en el primer input,
//     Esc cierra, role=dialog + aria-modal.
//   - Schema de fecha simple: input type=date + input type=time. El backend
//     espera ISO 8601 con offset, así que componemos `${date}T${time}:00` +
//     offset del navegador (vía Intl). No usamos `datetime-local` porque
//     Firefox lo renderiza inconsistente.
//   - En modo edit, botón "Borrar" con confirm() nativo (es destructivo y
//     baja-frecuencia: no merece custom modal anidado).
//   - Después de guardar/borrar OK, onClose() y las queries se invalidan
//     automáticamente desde el hook (DASHBOARD_QUERY_KEY + WORKOUTS_QUERY_KEY).
//   - No usamos react-hook-form (consistente con el resto de forms del repo).

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { X } from 'lucide-react';
import {
  INTENSITY_ZONES,
  PLANNED_DISCIPLINES,
  useCreatePlannedWorkout,
  useDeletePlannedWorkout,
  useUpdatePlannedWorkout,
  type IntensityZone,
  type PlannedDiscipline,
  type PlannedWorkout,
} from '@/lib/workouts';
import { disciplineLabel } from '@/features/dashboard/WorkoutCard';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type PlannedWorkoutFormMode = 'create' | 'edit';

export interface PlannedWorkoutFormProps {
  mode: PlannedWorkoutFormMode;
  /** Solo en `mode='edit'`: planned actual a editar. */
  initial?: PlannedWorkout;
  /** Solo en `mode='create'`: día propuesto (default = hoy). */
  defaultDate?: Date;
  /** Hora por defecto en formato `HH:mm` (default `07:00`). */
  defaultTime?: string;
  onClose: () => void;
}

export function PlannedWorkoutForm({
  mode,
  initial,
  defaultDate,
  defaultTime = '07:00',
  onClose,
}: PlannedWorkoutFormProps): ReactElement {
  const createMutation = useCreatePlannedWorkout();
  const updateMutation = useUpdatePlannedWorkout();
  const deleteMutation = useDeletePlannedWorkout();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  // --- State del form ------------------------------------------------------
  const initialDate = useMemo(() => {
    if (mode === 'edit' && initial) {
      return toDateInputValue(initial.datetime);
    }
    return toDateInputValue((defaultDate ?? new Date()).toISOString());
  }, [mode, initial, defaultDate]);
  const initialTime = useMemo(() => {
    if (mode === 'edit' && initial) {
      return toTimeInputValue(initial.datetime, initial.timezone);
    }
    return defaultTime;
  }, [mode, initial, defaultTime]);

  const [date, setDate] = useState<string>(initialDate);
  const [time, setTime] = useState<string>(initialTime);
  const [discipline, setDiscipline] = useState<PlannedDiscipline>(
    (initial?.discipline as PlannedDiscipline) ?? 'bike',
  );
  const [duration, setDuration] = useState<string>(
    String(initial?.durationPlannedMin ?? 60),
  );
  const [intensity, setIntensity] = useState<IntensityZone | ''>(
    (initial?.intensityZone as IntensityZone | null) ?? '',
  );
  const [description, setDescription] = useState<string>(
    initial?.description ?? '',
  );
  const [error, setError] = useState<string | null>(null);

  // --- Mount: focus + Esc para cerrar -------------------------------------
  useEffect(() => {
    firstFieldRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  // --- Submit --------------------------------------------------------------
  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const durMin = Number(duration);
    if (!Number.isFinite(durMin) || durMin < 1 || durMin > 1440) {
      setError('La duración debe estar entre 1 y 1440 minutos.');
      return;
    }
    if (!date || !time) {
      setError('Fecha y hora son obligatorias.');
      return;
    }

    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid';
    const datetime = composeISO(date, time);

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync({
          datetime,
          timezone,
          discipline,
          durationPlannedMin: Math.round(durMin),
          intensityZone: intensity === '' ? null : intensity,
          description: description.trim() === '' ? null : description.trim(),
        });
      } else {
        if (!initial) {
          setError('Estado inconsistente: edit sin initial.');
          return;
        }
        await updateMutation.mutateAsync({
          id: initial.id,
          patch: {
            datetime,
            timezone,
            discipline,
            durationPlannedMin: Math.round(durMin),
            intensityZone: intensity === '' ? null : intensity,
            description: description.trim() === '' ? null : description.trim(),
          },
        });
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'No se pudo guardar el entreno.',
      );
    }
  }

  async function handleDelete() {
    if (!initial) return;
    const ok = window.confirm(
      `¿Borrar el entreno de ${disciplineLabel(initial.discipline)} del ${date}? Esta acción no se puede deshacer.`,
    );
    if (!ok) return;
    setError(null);
    try {
      await deleteMutation.mutateAsync(initial.id);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'No se pudo borrar el entreno.',
      );
    }
  }

  // --- Render --------------------------------------------------------------
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(e) => {
        // Click fuera del dialog → cerrar (siempre que no esté guardando).
        if (e.target === e.currentTarget && !pending) onClose();
      }}
      data-testid="planned-workout-form-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="planned-form-title"
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
      >
        <header className="mb-4 flex items-start justify-between gap-3">
          <h2
            id="planned-form-title"
            className="text-lg font-semibold text-slate-900"
          >
            {mode === 'create' ? 'Nuevo entreno' : 'Editar entreno'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            aria-label="Cerrar"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Fecha + hora ------------------------------------------------- */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Fecha</span>
              <input
                ref={firstFieldRef}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Hora</span>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
              />
            </label>
          </div>

          {/* Disciplina --------------------------------------------------- */}
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Disciplina</span>
            <select
              value={discipline}
              onChange={(e) => setDiscipline(e.target.value as PlannedDiscipline)}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
            >
              {PLANNED_DISCIPLINES.map((d) => (
                <option key={d} value={d}>
                  {disciplineLabel(d)}
                </option>
              ))}
            </select>
          </label>

          {/* Duración + intensidad ---------------------------------------- */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="font-medium text-slate-700">
                Duración (min)
              </span>
              <input
                type="number"
                min={1}
                max={1440}
                step={5}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">
                Intensidad <span className="text-slate-400">(opcional)</span>
              </span>
              <select
                value={intensity}
                onChange={(e) =>
                  setIntensity(e.target.value as IntensityZone | '')
                }
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
              >
                <option value="">—</option>
                {INTENSITY_ZONES.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Descripción -------------------------------------------------- */}
          <label className="block text-sm">
            <span className="font-medium text-slate-700">
              Descripción <span className="text-slate-400">(opcional)</span>
            </span>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={5000}
              placeholder="Ej: rodaje suave en Z2, calentamiento 15 min..."
              className="mt-1 block w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
            />
          </label>

          {/* Error -------------------------------------------------------- */}
          {error && (
            <p role="alert" className="text-sm text-rose-700">
              {error}
            </p>
          )}

          {/* Acciones ----------------------------------------------------- */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            {mode === 'edit' ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={pending}
                className="rounded border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 disabled:opacity-50"
              >
                Borrar entreno
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={pending}
                className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={pending}
                className="rounded bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:opacity-50"
              >
                {pending ? 'Guardando…' : mode === 'create' ? 'Crear' : 'Guardar'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — composición/parseo de fechas
// ---------------------------------------------------------------------------

/** Devuelve `YYYY-MM-DD` en TZ local del navegador. */
function toDateInputValue(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Devuelve `HH:mm` en la timezone indicada (no en local del browser). */
function toTimeInputValue(iso: string, _timezone: string): string {
  // Usamos Intl con la TZ original para ser fiel a lo que el usuario guardó.
  // Si la TZ no se reconoce, caemos a TZ local.
  try {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: _timezone || undefined,
    });
    return fmt.format(new Date(iso));
  } catch {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
}

/**
 * Compone un ISO 8601 con offset del navegador a partir de `date` (YYYY-MM-DD)
 * y `time` (HH:mm). El backend reinterpreta la TZ; este offset solo evita
 * ambigüedades al cruzar el meridiano UTC.
 */
function composeISO(date: string, time: string): string {
  const local = new Date(`${date}T${time}:00`);
  if (!Number.isFinite(local.getTime())) {
    throw new Error(`Fecha/hora inválida: ${date} ${time}`);
  }
  const tzOffsetMin = -local.getTimezoneOffset();
  const sign = tzOffsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(tzOffsetMin);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${date}T${time}:00${sign}${hh}:${mm}`;
}
